import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { apiRequest } from "../../api/client";
import type { PublicSessionInfo } from "../../api/types";
import { usePwaInstall } from "../../hooks/usePwaInstall";
import { directionOf, t, type UiLang } from "../../i18n/strings";

const UI_LANGS: { code: UiLang; label: string }[] = [
  { code: "en", label: "English" },
  { code: "ar", label: "العربية" },
  { code: "fr", label: "Français" },
];

export default function JoinPage() {
  const params = useParams<{ code?: string }>();
  const navigate = useNavigate();
  const { available: installAvailable, promptInstall } = usePwaInstall();
  const [uiLang, setUiLang] = useState<UiLang>("en");
  const [code, setCode] = useState(params.code?.toUpperCase() ?? "");
  const [session, setSession] = useState<PublicSessionInfo | null>(null);
  const [selectedLang, setSelectedLang] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (params.code) void lookup(params.code);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.code]);

  async function lookup(rawCode: string) {
    const cleaned = rawCode.trim().toUpperCase();
    if (!cleaned) return;
    setLoading(true);
    setError(null);
    setSession(null);
    try {
      const info = await apiRequest<PublicSessionInfo>(`/public/sessions/${cleaned}`, { auth: false });
      setSession(info);
      setSelectedLang(info.targetLanguageCodes[0] ?? null);
    } catch {
      setError(t(uiLang, "notFound"));
    } finally {
      setLoading(false);
    }
  }

  // Defensive de-dupe: a session created before the source/target overlap
  // guard existed (or one edited outside this UI) could still list the same
  // language code twice — never show an attendee two identical buttons.
  const allLangs = session
    ? Array.from(
        new Map(
          [
            { code: session.sourceLanguageCode, isSource: true },
            ...session.targetLanguageCodes.map((c) => ({ code: c, isSource: false })),
          ].map((l) => [l.code, l]),
        ).values(),
      )
    : [];

  const langLabels: Record<string, string> = { en: "English", ar: "العربية", fr: "Français" };

  return (
    <div className="attendee-screen" dir={directionOf(uiLang)}>
      <div className="attendee-hero">
        <img src="/brand/afa-logo.png" alt="AFA — Arab Fertilizer Association" className="hero-logo" />
        <div className="hero-tagline">{t(uiLang, "poweredBy")}</div>
      </div>

      <div className="lang-switch" role="radiogroup" aria-label="Interface language">
        {UI_LANGS.map((l) => (
          <button
            key={l.code}
            type="button"
            role="radio"
            aria-checked={l.code === uiLang}
            className={`lang-switch-btn${l.code === uiLang ? " active" : ""}`}
            onClick={() => setUiLang(l.code)}
          >
            {l.label}
          </button>
        ))}
      </div>

      {installAvailable && (
        <button
          className="btn-ghost"
          style={{ marginBottom: 12, alignSelf: "center", display: "flex" }}
          onClick={() => void promptInstall()}
        >
          ⭳ {t(uiLang, "installApp")}
        </button>
      )}

      {!session && (
        <div className="card stack">
          <div>
            <h1 className="title">{t(uiLang, "joinTitle")}</h1>
            <p className="subtitle">{t(uiLang, "joinSubtitle")}</p>
          </div>
          <form
            className="stack"
            onSubmit={(e) => {
              e.preventDefault();
              void lookup(code);
            }}
          >
            <div className="field">
              <label htmlFor="code">{t(uiLang, "sessionCode")}</label>
              <input
                id="code"
                value={code}
                onChange={(e) => setCode(e.target.value.toUpperCase())}
                placeholder="e.g. JVRD2H"
                autoFocus
                autoCapitalize="characters"
                maxLength={16}
              />
            </div>
            {error && <div className="error-banner">{error}</div>}
            <button className="btn-primary" type="submit" disabled={loading || !code.trim()}>
              {loading ? "…" : t(uiLang, "joinButton")}
            </button>
          </form>
        </div>
      )}

      {session && (
        <div className="card stack">
          <div>
            <h1 className="title">{session.name}</h1>
            <p className="subtitle">
              {[session.conferenceName, session.venue, session.speakerName].filter(Boolean).join(" · ")}
            </p>
          </div>
          <div className="field">
            <label>{t(uiLang, "chooseLanguage")}</label>
            <div className="lang-grid">
              {allLangs.map((l) => (
                <button
                  key={l.code}
                  type="button"
                  className={`lang-choice${selectedLang === l.code ? " selected" : ""}`}
                  onClick={() => setSelectedLang(l.code)}
                >
                  {langLabels[l.code] ?? l.code.toUpperCase()}
                </button>
              ))}
            </div>
          </div>
          <button
            className="btn-primary"
            disabled={!selectedLang}
            onClick={() => navigate(`/listen/${session.sessionCode}/${selectedLang}`)}
          >
            {t(uiLang, "startListening")}
          </button>
          <div className="small-print" style={{ marginTop: 0 }}>{t(uiLang, "aiDisclaimer")}</div>
          <button className="btn-ghost" onClick={() => setSession(null)}>
            ← {t(uiLang, "sessionCode")}
          </button>
        </div>
      )}

      <div className="small-print">{t(uiLang, "poweredBy")}</div>
    </div>
  );
}
